/**
 * @module deploy/cloudflare
 * @description SDK-free Cloudflare deployment slice contract.
 */

import type { DeployAdapter, DeploymentTarget } from "@geenius/adapters";

import {
  createProviderDeployAdapter,
  isProviderDeploymentTarget,
  type ProviderDeployAdapter,
  validateProviderDeploymentTarget,
} from "./provider-slice";

/**
 * Deployment target accepted by the Cloudflare provider slice.
 */
export type CloudflareDeploymentTarget = DeploymentTarget & {
  readonly provider: "cloudflare";
};

/**
 * Cloudflare-scoped deployment adapter. Downstream wiring may accept a neutral
 * adapter, but the wrapped public slice only accepts Cloudflare targets.
 */
export type CloudflareDeployAdapter = ProviderDeployAdapter<"cloudflare">;

/**
 * SDK-free manifest describing Cloudflare deployment capabilities and required
 * environment variables.
 */
export interface CloudflareDeployProviderManifest {
  readonly provider: "cloudflare";
  readonly displayName: string;
  readonly runtime: "sdk-free-contract";
  readonly capabilities: readonly string[];
  readonly environmentVariables: readonly string[];
}

/**
 * Cloudflare deploy contract bundle with its manifest, wrapped adapter, and
 * target validator.
 */
export interface CloudflareDeployContract {
  readonly provider: "cloudflare";
  readonly manifest: CloudflareDeployProviderManifest;
  readonly adapter: CloudflareDeployAdapter;
  validateTarget(target: unknown): CloudflareDeploymentTarget;
}

/**
 * Provider manifest for SDK-free Cloudflare deployment adapters.
 */
export const CLOUDFLARE_DEPLOY_PROVIDER: CloudflareDeployProviderManifest = {
  provider: "cloudflare",
  displayName: "Cloudflare Workers",
  runtime: "sdk-free-contract",
  capabilities: [
    "project-deployments",
    "rollback",
    "deployment-lookup",
    "deployment-list",
    "health-check",
  ],
  environmentVariables: ["CLOUDFLARE_ACCOUNT_ID", "CLOUDFLARE_API_TOKEN"],
};

/**
 * Checks whether a deployment target belongs to the Cloudflare provider.
 */
export function isCloudflareDeploymentTarget(
  target: unknown,
): target is CloudflareDeploymentTarget {
  return isProviderDeploymentTarget("cloudflare", target);
}

/**
 * Validates and narrows a deployment target for the Cloudflare provider slice.
 */
export function validateCloudflareDeploymentTarget(
  target: unknown,
): CloudflareDeploymentTarget {
  return validateProviderDeploymentTarget("cloudflare", target);
}

/**
 * Wraps a generic deployment adapter with Cloudflare target and result guards.
 */
export function createCloudflareDeployAdapter(
  adapter: DeployAdapter,
): CloudflareDeployAdapter {
  return createProviderDeployAdapter({ provider: "cloudflare", adapter });
}

/**
 * Creates a Cloudflare deploy contract bundle from a provider-neutral adapter.
 */
export function createCloudflareDeployContract(
  adapter: DeployAdapter,
): CloudflareDeployContract {
  return {
    provider: "cloudflare",
    manifest: CLOUDFLARE_DEPLOY_PROVIDER,
    adapter: createCloudflareDeployAdapter(adapter),
    validateTarget: validateCloudflareDeploymentTarget,
  };
}
